import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for session expiration error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('user');
      // If unauthorized on protected API call, redirect to login
      if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/auth/callback')) {
        window.location.href = `${BACKEND_URL}/api/auth/login`;
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: () => {
    window.location.href = `${BACKEND_URL}/api/auth/login`;
  },
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

// Machines API
export const machinesAPI = {
  getAll: (params) => api.get('/machines', { params }),
  getById: (id) => api.get(`/machines/${id}`),
  getAlerts: (id, params) => api.get(`/machines/${id}/alerts`, { params }),
  create: (data) => api.post('/machines', data),
  update: (id, data) => api.put(`/machines/${id}`, data),
  delete: (id) => api.delete(`/machines/${id}`),
  getSensors: (id) => api.get(`/machines/${id}/sensors`),
  getSensorReadings: (id, sensorId, params) => api.get(`/machines/${id}/sensors/${sensorId}/readings`, { params }),
  addSensorReading: (id, sensorId, data) => api.post(`/machines/${id}/sensors/${sensorId}/readings`, data),
  getHealth: (id) => api.get(`/machines/${id}/health`),
};

// Plants API
export const plantsAPI = {
  getAll: () => api.get('/plants'),
  getById: (id) => api.get(`/plants/${id}`),
  create: (data) => api.post('/plants', data),
  update: (id, data) => api.put(`/plants/${id}`, data),
  delete: (id) => api.delete(`/plants/${id}`),
};

// SOPs API
export const sopsAPI = {
  getAll: (params) => api.get('/sops', { params }),
  getById: (id) => api.get(`/sops/${id}`),
  create: (data) => api.post('/sops', data),
  update: (id, data) => api.put(`/sops/${id}`, data),
  delete: (id) => api.delete(`/sops/${id}`),
  generate: (data) => api.post('/sops/generate', data),
  updateStatus: (id, status) => api.patch(`/sops/${id}/status`, { status }),
  approve: (id) => api.post(`/sops/${id}/approve`),
  getCategories: () => api.get('/sops/meta/categories'),
};
// Maintenance API
export const maintenanceAPI = {
  getAll: (params) => api.get('/maintenance', { params }),
  getById: (id) => api.get(`/maintenance/${id}`),
  create: (data) => api.post('/maintenance', data),
  start: (id) => api.post(`/maintenance/${id}/start`),
  completeStep: (id, stepIndex, data) => api.post(`/maintenance/${id}/steps/${stepIndex}/complete`, data),
  complete: (id, data) => api.post(`/maintenance/${id}/complete`, data, {
    headers: { 'Content-Type': undefined }
  }),
  addAttachment: (id, data) => api.post(`/maintenance/${id}/attachments`, data),
  getUpcoming: (days) => api.get('/maintenance/schedule/upcoming', { params: { days } }),
  getOverdue: () => api.get('/maintenance/schedule/overdue'),
  generateAiLog: (data) => api.post(`/maintenance/generate-ai-log`, data),
  getTechnicians: () => api.get('/maintenance/technicians'),
  assign: (id, technicianId) => api.post(`/maintenance/${id}/assign`, { technicianId }),
  uploadImage: (formData) => api.post('/maintenance/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getMachineManuals: (machineId) => api.get('/knowledge', { 
    params: { 
      machineId, 
      limit: 50,
      status: 'approved' // Only show documents ready for AI synthesis
    } 
  }),
  updateSop: (id, sopId) => api.put(`/maintenance/${id}/sop`, { sopId }),
  generateSopFromFix: (data) => api.post('/maintenance/generate-sop-from-fix', data),
};

export const workOrdersAPI = {
  getAll: (params) => api.get('/work-orders', { params }),
  create: (data) => api.post('/work-orders', data),
  getById: (id) => api.get(`/work-orders/${id}`),
  start: (id) => api.post(`/work-orders/${id}/start`),
};

// Knowledge API
export const knowledgeAPI = {
  search: (params) => api.get('/knowledge/search', { params }),
  getAll: (params) => api.get('/knowledge', { params }),
  getById: (id) => api.get(`/knowledge/${id}`),
  update: (id, data) => api.put(`/knowledge/${id}`, data),
  validate: (id, data) => api.post(`/knowledge/${id}/validate`, data),
  getTroubleshooting: (params) => api.get('/knowledge/troubleshooting/search', { params }),
  generateTroubleshooting: (data) => api.post('/knowledge/troubleshooting/generate', data),
  getMeta: () => api.get('/knowledge/meta/types'),
  delete: (id) => api.delete(`/knowledge/${id}`),
  update: (id, data) => api.put(`/knowledge/${id}`, data),
  updateStatus: (id, status) => api.patch(`/knowledge/${id}/status`, { status }), 
};

// Documents API
export const documentsAPI = {
  upload: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  bulkUpload: (formData) => api.post('/documents/upload/bulk', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getStatus: (id) => api.get(`/documents/${id}/status`),
  getContent: (id) => api.get(`/documents/${id}/content`),
  reprocess: (id) => api.post(`/documents/${id}/reprocess`),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Predictive API
export const predictiveAPI = {
  getMachineInsights: (machineId, params) => api.get(`/predictive/machines/${machineId}/insights`, { params }),
  getActiveInsights: (params) => api.get('/predictive/insights/active', { params }),
  acknowledge: (id) => api.post(`/predictive/insights/${id}/acknowledge`),
  action: (id, data) => api.post(`/predictive/insights/${id}/action`, data),
  analyze: (machineId) => api.post(`/predictive/machines/${machineId}/analyze`),
  getMetrics: (machineId, params) => api.get(`/predictive/machines/${machineId}/metrics`, { params }),
  getReliability: (machineId) => api.get(`/predictive/machines/${machineId}/reliability`),
  getSparePredictions: () => api.get('/predictive/spare-parts/predictions'),
};

// Assistant API
export const assistantAPI = {
  createSession: (data) => api.post('/assistant/sessions', data),
  chat: (data) => api.post('/assistant/chat', data),
  extractSop: (data) => api.post('/assistant/extract-sop', data),
  quickQuestion: (data) => api.post('/assistant/quick', data),
  getSession: (sessionId) => api.get(`/assistant/sessions/${sessionId}`),
  getSessions: (params) => api.get('/assistant/sessions', { params }),
  escalate: (sessionId, data) => api.post(`/assistant/sessions/${sessionId}/escalate`, data),
  feedback: (sessionId, data) => api.post(`/assistant/sessions/${sessionId}/feedback`, data),
  getSuggestions: (machineId) => api.get(`/assistant/suggestions/${machineId}`),
};

// Dashboard API
export const dashboardAPI = {
  getData: (params) => api.get('/dashboard', { params }),
  getAlerts: (params) => api.get('/dashboard/alerts', { params }),
  getPerformance: (params) => api.get('/dashboard/performance', { params }),
  getCompliance: () => api.get('/dashboard/compliance'),
  getStats: () => api.get('/dashboard/stats'),
};

// Training API
export const trainingAPI = {
  getModules: (params) => api.get('/training', { params }),
  getModule: (id) => api.get(`/training/${id}`),
  create: (data) => api.post('/training', data),
  update: (id, data) => api.put(`/training/${id}`, data),
  start: (id) => api.post(`/training/${id}/start`),
  updateProgress: (id, data) => api.post(`/training/${id}/progress`, data),
  complete: (id, data) => api.post(`/training/${id}/complete`, data),
  getCertifications: () => api.get('/training/user/certifications'),
  getStats: () => api.get('/training/stats/overview'),
};

// Workflows API
export const workflowsAPI = {
  getAll: (params) => api.get('/workflows', { params }),
  getById: (id) => api.get(`/workflows/${id}`),
  create: (data) => api.post('/workflows', data),
  approve: (id, data) => api.post(`/workflows/${id}/approve`, data),
  reject: (id, data) => api.post(`/workflows/${id}/reject`, data),
  addComment: (id, data) => api.post(`/workflows/${id}/comments`, data),
  assign: (id, data) => api.post(`/workflows/${id}/assign`, data),
  complete: (id, data) => api.post(`/workflows/${id}/complete`, data),
  createSpareRequisition: (data) => api.post('/workflows/auto/spare-requisition', data),
  getMyApprovals: () => api.get('/workflows/my/approvals'),
};

export const errorAPI = {
  log: (data) => api.post('/errors/log', data),
  getAll: (params) => api.get('/errors', { params }),
  getById: (id) => api.get(`/errors/${id}`),
};

export default api;
